import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';
import queryString from 'querystring';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { bemGenerator } from '@prisjakt/ui-base';
import { AppContext } from '@prisjakt/ui-app-context';

import ProductCard from '@prisjakt/ui-product-card';
import { SingleRange } from '@prisjakt/ui-filters';
import ProductImage from '@prisjakt/ui-product-image';

import PageContent from '../../components/page-content';

import CampaignBreadcrumb from '../campaign/components/breadcrumb';
import CampaignHeader from '../campaign/components/campaign-header';

import FetchMore from '../product-price-history/components/fetch-more';
import Chip from '../../components/chip';

import actions from './actions';

import './index.scss';

const styles = bemGenerator('gift-guide', [
  'filter',
  'lists',
  'price',
  'product-list',
]);

function GiftGuide({
  data: { campaign },
  fetchMoreData,
  giftGuideCampaignItemClick,
  location,
}) {
  const { t, renderMode } = useContext(AppContext);
  const qs = queryString.decode(location.search.substr(1));
  const realMinPrice = Math.floor(
    campaign.campaignItems.priceSummary.lowestPrice
  );
  const realMaxPrice = Math.ceil(
    campaign.campaignItems.priceSummary.highestPrice
  );
  const minPrice = parseFloat(qs.min_price || realMinPrice);
  const maxPrice = parseFloat(qs.max_price || realMaxPrice);

  const lists = campaign.campaignItems.groups
    ? campaign.campaignItems.groups.reduce((uniqueLists, list) => {
        return uniqueLists.find(uniqueList => uniqueList.id === list.id)
          ? uniqueLists
          : [...uniqueLists, list];
      }, [])
    : [];

  const activeList =
    campaign.campaignItems.groups &&
    campaign.campaignItems.groups.find(
      list =>
        location.pathname === `${campaign.web_uri.responsive}/${list.slug}`
    );

  return (
    <PageContent withAds>
      <section>
        {renderMode !== 'native' && <CampaignBreadcrumb campaign={campaign} />}

        <CampaignHeader isActive campaign={campaign} />

        <div>
          <FetchMore
            onFetchMoreData={() =>
              fetchMoreData({
                ...campaign.campaignItems.pageInfo,
                offset:
                  campaign.campaignItems.pageInfo.limit +
                  campaign.campaignItems.pageInfo.offset +
                  1,
              })
            }
            pageInfo={campaign.campaignItems.pageInfo}
          >
            <div className={styles.filter}>
              {campaign.campaignItems.groups && (
                <div className={styles.lists}>
                  <Chip key="all" active={!activeList}>
                    <Link to={campaign.web_uri.responsive}>
                      {t('category', 'subcategories.show-all')}
                    </Link>
                  </Chip>
                  {lists.map(list => (
                    <Chip key={list.id} active={list === activeList}>
                      <Link to={`${campaign.web_uri.responsive}/${list.slug}`}>
                        {list.name}
                      </Link>
                    </Chip>
                  ))}
                </div>
              )}
              <div className={styles.price}>
                <h4>{t('common', 'sort.price')}</h4>
                <SingleRange
                  values={{
                    values: [{ from: realMinPrice, to: realMaxPrice }],
                    min_values: realMinPrice,
                    max_values: realMaxPrice,
                  }}
                  selected={{ from: minPrice, to: maxPrice }}
                  onChange={({ value }) => {
                    window.__history.push(
                      value
                        ? `?min_price=${value.from}&max_price=${value.to}`
                        : location.pathname
                    );
                  }}
                  showBars={false}
                  type="integer"
                />
              </div>
            </div>
            <div className={styles['product-list']}>
              {campaign.campaignItems.nodes.map(
                ({ product }, index) =>
                  product && (
                    <ProductCard
                      key={`${product.id}-${index.toString()}`}
                      link={product.pathName}
                      name={product.name}
                      onClick={() => {
                        giftGuideCampaignItemClick({
                          campaign,
                          product,
                        });
                      }}
                      image={
                        <ProductImage
                          name={product.name}
                          src={product.media.first}
                        />
                      }
                      ratingCount={product.userReviewSummary.count}
                      rating={product.userReviewSummary.rating}
                      price={
                        product.priceSummary.regular ||
                        product.priceSummary.alternative
                      }
                      externalLink={['desktop', 'mobile', 'native'].includes(
                        renderMode
                      )}
                    />
                  )
              )}
            </div>
          </FetchMore>
        </div>
      </section>
    </PageContent>
  );
}

const { func, object, shape } = PropTypes;

GiftGuide.defaultProps = {
  fetchMoreData: () => {},
};

GiftGuide.propTypes = {
  data: shape({
    campaign: object.isRequired,
  }).isRequired,
  fetchMoreData: func,
  location: object.isRequired,
  giftGuideCampaignItemClick: func.isRequired,
};

export default withRouter(connect(undefined, actions)(memo(GiftGuide)));
